import { autoinject } from 'aurelia-dependency-injection';
import * as signalR from "@microsoft/signalr";
import { ConfigService } from './config.service';
import { AppInsightsService } from '../appinsights/appinsights-service';

@autoinject
export class SignalClientService {

    private _identityKey: string;
    private _connection: signalR.HubConnection;
    private _subscriptions: { methodName: string, originalCallback: (data) => void, subscribedCallback: (data) => void }[] = [];
    private _joinedGroups: string[] = [];
    private _reconnectIntervalIncrement: number = 5000;
    private _reconnectInterval: number = 5000;

    constructor(private configService: ConfigService, private appInsightsService: AppInsightsService) {
        window.onbeforeunload = () => {
            for (const group of this._joinedGroups)
                this.notifyServer("SignalHubInterceptor", "LeaveGroup", {
                    group: group
                });
        };
    }

    async stopListener(): Promise<void> {
        if (this.isConnected) {
            await this._connection.stop();
        }
    }

    wrapCallback(methodName: string, callback: (data) => void | Promise<void>) {
        return async (data) => {
            const refreshOnActiveTab = async () => {
                const response = callback(data);
                if (response instanceof Promise) {
                    await response;
                }
                document.removeEventListener('visibilitychange', refreshOnActiveTab);
            }

            if (document.visibilityState === 'visible') {
                refreshOnActiveTab();
            } else {
                document.addEventListener('visibilitychange', refreshOnActiveTab);
            }

            await this.appInsightsService.logInformation("Received SignalR message for: " + methodName);
        }
    }

    subscribe(methodName: string, c: (data) => void): void {
        const callback = this.wrapCallback(methodName, c);
        this.unsubscribe(methodName, callback);
        this._subscriptions.push({ methodName, originalCallback: c, subscribedCallback: callback });
        if (this._connection) {
            this._connection.on(methodName, callback);
        }
    }

    unsubscribe(methodName: string, callback: (data) => void): void {
        const subscription = this._subscriptions.filter(x => x.methodName == methodName && x.originalCallback == callback)[0];
        if (this._connection && subscription) {
            this._connection.off(methodName, subscription.subscribedCallback);
        }
        let index = -1;

        while (index = this._subscriptions.findIndex(x => x.methodName == methodName && x.originalCallback == callback), index > -1) {
            this._subscriptions.splice(index, 1);
        }
    }

    async subscribeToGroup(methodName: string, groupName: string, callback: (data) => void): Promise<void> {
        await this.subscribeToGroups(methodName, [groupName], callback);
    }

    async unsubscribeToGroup(methodName: string, groupName: string, callback: (data) => void): Promise<void> {
        await this.unsubscribeToGroups(methodName, [groupName], callback);
    }

    async subscribeToGroups(methodName: string, groupNames: string[], callback: (data) => void): Promise<void> {
        this.subscribe(methodName, callback);
        for (const groupName of groupNames) {
            const alreadyJoined = this._joinedGroups.indexOf(groupName) > -1;
            if (!alreadyJoined) {
                this._joinedGroups.push(groupName);
                console.log(`Joined Group - ${groupName}`);
                await this.notifyServer("SignalHubInterceptor", "JoinGroup", {
                    group: groupName
                });
            }
        }
    }

    async unsubscribeToGroups(methodName: string, groupNames: string[], callback: (data) => void): Promise<void> {
        this.unsubscribe(methodName, callback);
        for (const groupName of groupNames) {
            const index = this._joinedGroups.indexOf(groupName);
            const alreadyJoined = index > -1;
            if (alreadyJoined) {
                this._joinedGroups.splice(index, 1);
                console.log(`Left Group - ${groupName}`);
                await this.notifyServer("SignalHubInterceptor", "LeaveGroup", {
                    group: groupName
                });
            }
        }
    }

    async notifyServer(interceptorName: string, functionName: string, data: any): Promise<void> {
        try {
            if (this._connection) {

                if (!this.isConnected) {
                    await this.reconnect();
                    await this._connection.invoke('InvokeServerFunction', interceptorName, functionName, data);
                }
                else {
                    await this._connection.invoke('InvokeServerFunction', interceptorName, functionName, data);
                }

            }
        }
        catch (e) {
            console.error(`Error invoking server function: ${e.message}`);

            // if the connection is not open, try to reconnect and then send the message
            if (!this.isConnected) {
                await this.reconnect();
                await this._connection.invoke('InvokeServerFunction', interceptorName, functionName, data);
            }
        }
    }

    private delay(time: number): Promise<void> {
        return new Promise<void>((resolve) => {
            setTimeout(resolve, time);
        });
    }

    private async reconnect(): Promise<any> {
        while (!this.isConnected) {
            try {
                await this.delay(this._reconnectInterval);
                console.log(`Reconnecting to signalR...`);
                await this.startListener(this._identityKey);
            }
            finally {
                if (this.isConnected) {
                    console.log(`Reconnected to signalR`);
                } else {
                    this._reconnectInterval += this._reconnectIntervalIncrement;
                    //If the interval is greater than 1 minute, reset it to 5 seconds
                    if (this._reconnectInterval > 60000) {
                        this._reconnectInterval = this._reconnectIntervalIncrement;
                    }
                    console.error(`Error trying to re-open signalR connection, waiting ${this._reconnectInterval / 1000} sec before retrying...`);
                }
            }
        }
    }

    private onClose = async (e: Error): Promise<void> => {
        if (e) {
            console.warn(`SignalR connection closed with message "${e.message}"`);
        } else {
            console.log("SignalR connection closed");
        }
        await this.reconnect();
    }

    private getConnection(): signalR.HubConnection {
        const queryString = this._identityKey ? `?identityKey=${this._identityKey}` : "";
        const connection: signalR.HubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${this.configService.config.urls.rightShipPlatformApiUrl}/genericsr${queryString}`)
            .configureLogging(signalR.LogLevel.None)
            .build();
        connection.onclose(this.onClose);
        return connection;
    }

    async startListener(identityKey: string = null): Promise<boolean> {
        if (this.isConnected) {
            return true;
        }

        this._identityKey = identityKey;

        try {
            this._connection = this.getConnection();
            await this._connection.start();
            console.log("signalR connection opened");
            if (this.isConnected) {
                for (const x of this._subscriptions) {
                    this._connection.on(x.methodName, x.subscribedCallback);
                }

                for (const groupName of this._joinedGroups) {
                    console.log(`Rejoining Group - ${groupName}`);
                    await this.notifyServer("SignalHubInterceptor", "JoinGroup", {
                        group: groupName
                    });
                }
            }
            this._reconnectInterval = this._reconnectIntervalIncrement;
            return true;
        } catch (err) {
            console.error(`Error opening SignalR websocket connection: ${err ? err.message : ''}`);
            return false;
        }
    }

    get isConnected(): boolean {
        return this._connection && this._connection.state == "Connected";
    }
}

export const SignalFunctions = {
    onSyncTaskComplete: "on-sync-task-complete",
    refreshComments: "RefreshComments",
}